import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import {
  MeasurementUnits,
  PatientSuggestionsPerDiseaseMetrics,
  QualifiedFeature,
  Qualifier,
} from "fe-common/graphql";
import { isQualifierEqual } from "../../../util/QualifierComparator";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { Relations } from "shared";
import { useSearchQualifiers } from "../hooks/useSearchQualifiers";
import { useSortQualifiersBySuggestions } from "../hooks/useSortQualifiersBySuggestions";
import { SuggestionDiseaseStrength } from "../hooks/useSuggestions";
import {
  useOrderRelations,
  useSetDisabledQualifiers,
  useSetSelectedQualifiers,
} from "./refinementHooks";
import { RefineByRelation, RelationName } from "./refineByRelation";
import { styled, Theme } from "@mui/material";
import { DurationInput } from "./durationInput";
import { useGetDuration } from "./useIsChronicAccute";
import { ThreeDotsLoader } from "fe-web-components/loader/threeDotsLoader";
import { MobileView } from "../../../util/aKaunGeneralUtil";
import { YesNoButtonComponent } from "../common/YesNoButtonComponent";
import { RosebudMobileBottomNavigationHeight } from "../common/rosebudButtomNavigationButton";
import { dedupQualifiers } from "fe-common/logic/qualifiedFeature/dedupQualifiers";
import { featureTo2Lines } from "fe-common/logic";
import { CheckboxButtonComponent } from "../common/CheckboxYesNoComponent";
import { ClinicalReasoningTooltip } from "../common/ClinicalReasoningTooltip";
import { RANGE_QUALIFIER_FEATURE_ID } from "fe-common/graphql/dao";

//region [[ Styles ]]

const RefineFeaturePanelView = styled("div")(({ theme }) => ({
  minWidth: "400px",
  maxWidth: "600px",
  paddingBottom: "100px",
  [theme.breakpoints.down(MobileView)]: {
    width: "auto",
    height: "auto",
    minWidth: "240px",
    paddingBottom: `${RosebudMobileBottomNavigationHeight}px`,
  },
}));

const LoaderContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "100%",
}));

const YesNoContainer = styled("div")(({ theme }) => ({
  marginBottom: theme.spacing(2),
  display: "flex",
  flexFlow: "wrap",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
}));

const DurationContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginBottom: theme.spacing(2),
}));

const RefineQuestionsPanel = styled(
  "div",
)(({ theme }: { theme?: Theme }) => ({}));

const YesNoFindingContainer = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
}));

export const MobileClinicalReasoning = styled(ClinicalReasoningTooltip)(
  ({ theme }) => ({
    marginBottom: "0px",
    padding: "8px",
    margin: "10px",
    marginLeft: "5px",

    [theme.breakpoints.down(MobileView)]: {
      padding: "0px",
      marginTop: "10px",
      margin: "0px",
    },
  }),
);

//endregion [[ Styles ]]

//region [[ Props ]]

export interface RefineFeaturePanelProps {
  className?: string;
  onChange: (qs: Qualifier[]) => any;
  onChangeNegate?: (resp: any) => any;
  selectedQualifiers: Qualifier[];
  qf: QualifiedFeature;
  causedByDisease?: (PatientSuggestionsPerDiseaseMetrics &
    SuggestionDiseaseStrength)[];
  showDuration: boolean;
  showTitle?: boolean;
}

export type QualifierWithSelection = {
  qualifier: Qualifier;
  selected: boolean;
  disabled?: boolean;
  negated?: boolean;
  canBeNegated?: boolean;
};

//endregion [[ Props ]]

//region [[ Functions ]]
const durationQualifier = (
  time: number,
  units: MeasurementUnits,
): Qualifier => ({
  relation: {
    id: Relations.DURATION,
    name: "Duration",
    type: null,
    multi: false,
    essence: null,
  },
  freeText: "",
  source: null,
  name: `Duration ${time}-${time} ${units}`,
  range: {
    low: time,
    high: time,
    units: units,
    complexUnits: null,
  },
  value: {
    name: `${time}-${time} ${units}`,
    shortname: `${time}-${time} ${units}`,
    patientDescription: "",
    description: { text: "" },
    category: null,
    id: RANGE_QUALIFIER_FEATURE_ID,
    parents: {
      features: [],
    },
    range: null,
  },
  negate: false,
});

//endregion [[ Functions ]]

export const RefineFeaturePanel = ({ ...props }: RefineFeaturePanelProps) => {
  const { onChange, selectedQualifiers, causedByDisease, qf } = props;
  const NUM_RELATIONS = 2;
  const [numRelations, setNumRelations] = useState(NUM_RELATIONS);
  const feature2Lines = featureTo2Lines(props.qf);
  const refineQfText = feature2Lines ? feature2Lines[0] : "";

  const onAdd = useCallback(
    (q) => {
      const qualifierToChangeIndex = selectedQualifiers.findIndex(
        (sq) =>
          sq.relation.id === q.relation.id && sq.value?.id === q.value?.id,
      );

      if (qualifierToChangeIndex > -1) {
        const clonedQualifiers = [...selectedQualifiers];
        clonedQualifiers[qualifierToChangeIndex].negate = q.negate;

        onChange(dedupQualifiers([...clonedQualifiers]));
      } else {
        onChange(dedupQualifiers([...selectedQualifiers, q]));
      }

      evt({
        category: CATEGORY.AGENT_WIZARD,
        action: ACTION.ADD_REFINE,
        label: q.value.name + " to " + qf.name,
      });
    },
    [selectedQualifiers, onChange, qf],
  );

  const onRemove = useCallback(
    (qualifiedFeature: Qualifier | Qualifier[]) => {
      const qsToRemoveArray = Array.isArray(qualifiedFeature)
        ? qualifiedFeature
        : [qualifiedFeature];

      onChange(
        selectedQualifiers.filter(
          (sq) => !qsToRemoveArray.some((q2) => isQualifierEqual(sq, q2)),
        ),
      );

      qsToRemoveArray.forEach((q) =>
        evt({
          category: CATEGORY.AGENT_WIZARD,
          action: ACTION.REMOVE_REFINE,
          label: q.value.name + " from " + qf.name,
        }),
      );
    },
    [selectedQualifiers, onChange, qf],
  );

  const id = qf?.feature?.id;
  const relationIds = useMemo(
    () =>
      qf?.qualifiers
        .filter((q) => !q.value)
        .map((q) => q.relation.id)
        .sort(),
    [qf],
  );
  let { qualifiers, loading } = useSearchQualifiers(id, relationIds);
  let qualifiersWithSelection = useSortQualifiersBySuggestions(
    qf.feature.id,
    qualifiers,
    selectedQualifiers,
    causedByDisease,
  );

  qualifiersWithSelection = useSetSelectedQualifiers(
    qualifiersWithSelection,
    selectedQualifiers,
  );

  useSetDisabledQualifiers(qualifiersWithSelection);

  const orderedRelations = useOrderRelations(qualifiersWithSelection);

  const onRefineMore = () => {
    setNumRelations(orderedRelations.length);
  };

  const onSingleSelect = useCallback(
    (q) => {
      const filteredQualifiers = selectedQualifiers.filter(
        (q1) => q1.relation.id !== q.relation.id,
      );
      onChange([...filteredQualifiers, q]);
    },
    [selectedQualifiers, onChange],
  );

  const onDurationChange = useCallback(
    (time: number, units: MeasurementUnits) => {
      const q = durationQualifier(time, units);
      onSingleSelect(q);
    },
    [onSingleSelect],
  );

  const onDurationClear = useCallback(
    (time: number, units: MeasurementUnits) => {
      const q = durationQualifier(time, units);
      onRemove([q]);
    },
    [onRemove],
  );

  const [time, timeUnit] = useGetDuration(selectedQualifiers);

  if (!qf) return null;

  return (
    <RefineFeaturePanelView
      className={"refine-panel" + (props.className || "")}
    >
      {loading ? (
        <LoaderContainer>
          <ThreeDotsLoader />
        </LoaderContainer>
      ) : (
        <>
          {props.onChangeNegate && (
            <YesNoContainer>
              <YesNoFindingContainer>
                <CheckboxButtonComponent
                  qf={props.qf}
                  onChange={() => {}}
                  disabled={false}
                  dataInput={{ negate: true }}
                />
                <RelationName className={"relation-name"}>
                  {refineQfText}
                </RelationName>
              </YesNoFindingContainer>
              <YesNoButtonComponent
                qf={qf}
                onChange={props.onChangeNegate}
                disabled={false}
                response={{ negate: qf.negate }}
              />
            </YesNoContainer>
          )}

          <RefineQuestionsPanel>
            {props.showDuration && (
              <DurationContainer className={"duration-container"}>
                <RelationName className={"relation-name"}>
                  Duration of symptom
                </RelationName>
                <DurationInput
                  time={time}
                  disabled={qf.negate}
                  timeUnit={timeUnit || MeasurementUnits.Days}
                  onChange={onDurationChange}
                  onClear={onDurationClear}
                />
              </DurationContainer>
            )}
            {props.showTitle && <RelationName>Refine</RelationName>}
            <RefineByRelation
              disabled={qf.negate}
              qf={qf}
              onRemove={onRemove}
              onAdd={onAdd}
              qualifiers={qualifiersWithSelection}
              numRelations={numRelations}
              hideClinicalReasoning={!causedByDisease}
              causedByDisease={causedByDisease}
              onRefineMore={onRefineMore}
              orderedRelations={orderedRelations}
            />
          </RefineQuestionsPanel>
        </>
      )}
    </RefineFeaturePanelView>
  );
};
